import React from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Clock,
  Users,
  DollarSign,
  Check,
  Eye,
  UserPlus,
  X,
  MessageCircle
} from "lucide-react";

// Job skill icons mapping
const JOB_SKILLS = {
  "Hero": [{ name: "Advanced Combo", icon: "https://legends.ml/static/images/lib/skill/1120003.png" }],
  "Dark Knight": [{ name: "Hyper Body", icon: "https://legends.ml/static/images/lib/skill/1301007.png" }],
  "Paladin": [{ name: "Total Crash", icon: "https://legends.ml/static/images/lib/skill/1221007.png" }],
  "Arch Mage (Fire/Poison)": [{ name: "Meteor Shower", icon: "https://legends.ml/static/images/lib/skill/2121007.png" }],
  "Arch Mage (Ice/Lightning)": [{ name: "Blizzard", icon: "https://legends.ml/static/images/lib/skill/2221007.png" }],
  "Bishop": [
    { name: "Resurrection", icon: "https://legends.ml/static/images/lib/skill/2321006.png" },
    { name: "Holy Symbol", icon: "https://legends.ml/static/images/lib/skill/2311003.png" }
  ],
  "Bowmaster": [{ name: "Sharp Eyes", icon: "https://legends.ml/static/images/lib/skill/3121002.png" }],
  "Marksman": [{ name: "Sharp Eyes", icon: "https://legends.ml/static/images/lib/skill/3221002.png" }],
  "Night Lord": [{ name: "Triple Throw", icon: "https://legends.ml/static/images/lib/skill/4121007.png" }],
  "Shadower": [{ name: "Smoke Screen", icon: "https://legends.ml/static/images/lib/skill/4221006.png" }],
  "Buccaneer": [{ name: "Time Leap", icon: "https://legends.ml/static/images/lib/skill/5121010.png" }],
  "Corsair": [{ name: "Battleship", icon: "https://legends.ml/static/images/lib/skill/5221006.png" }]
};

// Status badge colors
const getStatusColor = (status) => {
  switch (status) {
    case "ACTIVE": return "bg-emerald-600 text-white";
    case "SUCCEEDED": return "bg-amber-500 text-black";
    case "SETTLED": return "bg-purple-600 text-white";
    case "INACTIVE": return "bg-stone-500 text-white";
    case "CANCELLED": return "bg-red-600 text-white";
    default: return "bg-stone-500 text-white";
  }
};

const formatHP = (hp) => {
  if (hp >= 1000000000) return `${(hp / 1000000000).toFixed(1)}B`;
  if (hp >= 1000000) return `${(hp / 1000000).toFixed(1)}M`;
  if (hp >= 1000) return `${(hp / 1000).toFixed(1)}K`;
  return hp.toString();
};

// Format mesos: input is in millions, display as "X,000,000 mesos"
const formatMesos = (value) => {
  if (!value && value !== 0) return "";
  const num = parseFloat(value);
  if (isNaN(num)) return value; // fallback for legacy text values
  const full = num * 1000000;
  return `${full.toLocaleString()} mesos`;
};

const ExpeditionPoster = ({
  expedition,
  isHunter = false,
  isLeader = false,
  isAdmin = false,
  hasPendingInvite = false,
  isMember = false,
  isParticipant = false,
  onApply,
  onApplyAsClient,
  onAcceptInvite,
  onLeave,
  onViewDetails,
  onConfirm,
  onAdminCancel,
  onViewUser,
  bossImage,
  parchmentBg
}) => {
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const ukTime = date.toLocaleString("en-GB", {
      timeZone: "UTC",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "short"
    });
    return ukTime;
  };

  const formatDateTimeLocal = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-GB", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "short"
    });
  };
  
  const memberCount = (expedition.members?.filter(m => m.status === "accepted").length || 0) + 1; // +1 for leader
  const totalSlots = expedition.total_slots || 6;
  
  return (
    <div
      className="relative rounded-lg overflow-hidden shadow-2xl transform hover:scale-[1.02] transition-all cursor-pointer"
      style={{
        background: "linear-gradient(180deg, #1a237e 0%, #0d1b4a 50%, #0a0f2c 100%)",
        border: "3px solid #3949ab",
        boxShadow: "0 8px 32px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1)"
      }}
      onClick={onViewDetails}
    >
      {/* Glowing top border */}
      <div 
        className="absolute top-0 left-0 right-0 h-1"
        style={{ background: "linear-gradient(90deg, transparent, #7c4dff, transparent)" }}
      />
      
      {/* Content */}
      <div className="relative p-5 min-h-[420px] flex flex-col">
        {/* Status Badge */}
        <div className="absolute top-3 right-3 z-10">
          <Badge className={`${getStatusColor(expedition.status)} font-bold text-xs shadow-lg px-2 py-1`}>
            {expedition.status}
          </Badge>
        </div>
        
        {/* EXPEDITION Title */}
        <div className="text-center mb-3">
          <h3 
            className="text-2xl font-black tracking-widest"
            style={{ 
              color: "#7c4dff",
              textShadow: "0 0 20px rgba(124,77,255,0.5), 0 2px 4px rgba(0,0,0,0.5)"
            }}
          >
            EXPEDITION
          </h3>
          <div className="text-[10px] text-blue-300/70 tracking-[0.3em]">LOOKING FOR GROUP</div>
        </div>
        
        {/* Boss Image with glow */}
        <div className="flex justify-center mb-3">
          <div 
            className="w-28 h-28 rounded-lg flex items-center justify-center"
            style={{
              background: "linear-gradient(180deg, #1e3a8a 0%, #1e1b4b 100%)",
              border: "3px solid #3b82f6",
              boxShadow: "0 0 20px rgba(59,130,246,0.3), inset 0 2px 4px rgba(0,0,0,0.3)"
            }}
          >
            <img
              src={bossImage || expedition.boss_image}
              alt={expedition.boss_name}
              className="w-20 h-20 object-contain"
              onError={(e) => {
                e.target.src = "https://legends.ml/static/images/lib/monster/6130101.png";
              }}
            />
          </div>
        </div>
        
        {/* Boss Name */}
        <div className="text-center mb-2">
          <h4 className="text-xl font-bold text-blue-100">
            {expedition.boss_name}
          </h4>
          <div className="flex justify-center gap-3 text-xs text-blue-300/80 mt-1">
            <span>HP: {formatHP(expedition.boss_hp)}</span>
            <span>•</span>
            <span>Rec: {expedition.boss_recommended_hp?.toLocaleString()} HP</span>
          </div>
        </div>
        
        {/* Price & Organizer Options */}
        <div 
          className="text-center mb-3 p-2 rounded-lg mx-4 space-y-2"
          style={{ background: "rgba(124,77,255,0.15)", border: "1px solid rgba(124,77,255,0.3)" }}
        >
          {/* Cost line - mesos + alternatives with "or" */}
          {expedition.selling_service !== false && (expedition.price_for_client || expedition.apr_required || expedition.onyx_apple_required) && (
            <div>
              <p className="text-[10px] text-blue-300/70 uppercase tracking-wider mb-1">Cost</p>
              <div className="flex items-center justify-center gap-2 flex-wrap">
                {expedition.price_for_client && (
                  <span className="text-lg font-bold text-emerald-400">{formatMesos(expedition.price_for_client)}</span>
                )}
                {expedition.price_for_client && (expedition.apr_required || expedition.onyx_apple_required) && (
                  <span className="text-xs text-blue-300/50 font-medium">or</span>
                )}
                {expedition.apr_required && (
                  <div className="flex items-center gap-1 px-2 py-0.5 bg-purple-500/20 rounded border border-purple-500/30">
                    <img src="https://legends.ml/static/images/lib/item/05050000.png" alt="APR" className="w-4 h-4" />
                    <span className="text-xs text-purple-300 font-bold">{expedition.apr_amount ? `${expedition.apr_amount}x` : ""} APR</span>
                  </div>
                )}
                {expedition.apr_required && expedition.onyx_apple_required && (
                  <span className="text-xs text-blue-300/50 font-medium">or</span>
                )}
                {expedition.onyx_apple_required && (
                  <div className="flex items-center gap-1 px-2 py-0.5 bg-purple-500/20 rounded border border-purple-500/30">
                    <img src="https://royals-library.netlify.app/images/items/02022179.png" alt="Onyx" className="w-4 h-4" />
                    <span className="text-xs text-purple-300 font-bold">{expedition.onyx_apple_amount ? `${expedition.onyx_apple_amount}x` : ""} Onyx Apple</span>
                  </div>
                )}
              </div>
            </div>
          )}
          {expedition.selling_service !== false && !expedition.price_for_client && !expedition.apr_required && !expedition.onyx_apple_required && (
            <div>
              <p className="text-[10px] text-blue-300/70 uppercase tracking-wider mb-1">Cost</p>
              <span className="text-lg font-bold text-emerald-400">Free</span>
            </div>
          )}
          {expedition.selling_service === false && (
            <div className="flex items-center justify-center gap-1">
              <span className="text-xs text-gray-300 font-bold">No Buyers — Hunters Only</span>
            </div>
          )}
          {/* Organizer badges row */}
          {(expedition.hb_provided || expedition.hp_gear_provided || expedition.safe_run_guaranteed || expedition.expected_runtime) && (
            <div className="flex items-center justify-center gap-2 flex-wrap">
              {expedition.hb_provided && (
                <div className="flex items-center gap-1 px-2 py-0.5 bg-pink-500/20 rounded border border-pink-500/30">
                  <img src="https://legends.ml/static/images/lib/skill/1301007.png" alt="HB" className="w-4 h-4" />
                  <span className="text-[11px] text-pink-300 font-bold">HB</span>
                </div>
              )}
              {expedition.hp_gear_provided && (
                <div className="flex items-center gap-1 px-2 py-0.5 bg-orange-500/20 rounded border border-orange-500/30">
                  <img src="https://royals-library.netlify.app/images/skills/0001001.png" alt="HP Gear" className="w-4 h-4" />
                  <span className="text-[11px] text-orange-300 font-bold">HP{expedition.hp_gear_value ? ` +${expedition.hp_gear_value}` : ""}</span>
                </div>
              )}
              {expedition.safe_run_guaranteed && (
                <div className="flex items-center gap-1 px-2 py-0.5 bg-emerald-500/20 rounded border border-emerald-500/30">
                  <img src="https://royals-library.netlify.app/images/skills/2321006.png" alt="Safe" className="w-4 h-4" />
                  <span className="text-[11px] text-emerald-300 font-bold">Safe Run</span>
                </div>
              )}
              {expedition.expected_runtime && (
                <div className="flex items-center gap-1 px-2 py-0.5 bg-blue-500/20 rounded border border-blue-500/30">
                  <span className="text-[11px] text-blue-300 font-bold">~{expedition.expected_runtime} min</span>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Expected Time */}
        <div className="text-center mb-3">
          <div className="flex items-center justify-center gap-2 text-blue-200">
            <Clock className="w-4 h-4" />
            <span className="font-semibold">{formatDateTime(expedition.expected_time)}</span>
          </div>
          <p className="text-[10px] text-gray-400/60 mt-0.5">Your time: {formatDateTimeLocal(expedition.expected_time)}</p>
        </div>
        
        {/* Custom Message */}
        {expedition.message && (
          <div className="mb-3 px-2">
            <p className="text-xs text-blue-200/80 text-center italic">
              <MessageCircle className="w-3 h-3 inline mr-1" />
              "{expedition.message}"
            </p>
          </div>
        )}
        
        {/* Job Requirements - Display with skill icons */}
        <div className="mb-3">
          <p className="text-[10px] text-blue-300/70 text-center uppercase tracking-wider mb-2">
            {expedition.free_for_all ? "Open to All Jobs" : "Looking For"}
          </p>
          
          {expedition.free_for_all ? (
            <div className="text-center">
              <Badge className="bg-emerald-500/30 text-emerald-300 border border-emerald-500/50">
                <Users className="w-3 h-3 mr-1" />
                Free For All - Any Job Welcome
              </Badge>
            </div>
          ) : expedition.job_requirements && expedition.job_requirements.length > 0 ? (
            <div className="flex flex-wrap justify-center gap-2">
              {expedition.job_requirements.map((req, i) => {
                const skills = JOB_SKILLS[req.job] || [];
                return (
                  <div 
                    key={i}
                    className="flex items-center gap-1 px-2 py-1 rounded bg-indigo-500/20 border border-indigo-500/40"
                  >
                    {skills.map((skill, si) => (
                      <img 
                        key={si}
                        src={skill.icon} 
                        alt={skill.name}
                        className="w-5 h-5"
                        title={`${req.job} - ${skill.name}`}
                      />
                    ))}
                    <span className="text-xs text-indigo-200">
                      {req.count > 1 ? `×${req.count}` : ""}
                      {req.min_level ? ` Lv.${req.min_level}+` : ""}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center text-xs text-blue-300/50">No specific requirements</div>
          )}
        </div>
        
        {/* Squad Slots */}
        <div 
          className="flex justify-center items-center gap-4 mb-3 p-3 rounded-lg"
          style={{ background: "rgba(0,0,0,0.3)" }}
        >
          <div className="text-center">
            <div className="flex items-center justify-center gap-1">
              <Users className="w-5 h-5 text-blue-400" />
              <span className="font-black text-2xl text-blue-300">{memberCount}/{totalSlots}</span>
            </div>
            <p className="text-[10px] text-blue-300/70 uppercase">Slots Filled</p>
          </div>
          {expedition.has_client && (
            <>
              <div className="w-px h-8 bg-blue-500/30" />
              <div className="flex items-center gap-1 px-2 py-1 bg-amber-500/20 rounded border border-amber-500/30">
                <DollarSign className="w-4 h-4 text-amber-400" />
                <span className="text-xs text-amber-300 font-bold">Client Joined</span>
              </div>
            </>
          )}
        </div>
        
        {/* Leader info */}
        <div className="text-center mb-3">
          <div className="flex items-center justify-center gap-2">
            {expedition.leader_avatar ? (
              <img src={expedition.leader_avatar} alt="" className="w-6 h-6 rounded-full border-2 border-blue-500" />
            ) : (
              <Users className="w-4 h-4 text-blue-300" />
            )}
            <span className="text-sm text-blue-200">
              <span className="text-blue-400">Leader:</span>{" "}
              <span
                className="cursor-pointer hover:text-blue-100 hover:underline"
                onClick={(e) => { e.stopPropagation(); onViewUser?.(expedition.leader_id); }}
                data-testid={`view-leader-${expedition.id}`}
              >
                {expedition.leader_username}
              </span>
            </span>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="mt-auto space-y-2" onClick={(e) => e.stopPropagation()}>
          {/* Accept Invitation button (when user has pending invite) */}
          {hasPendingInvite && onAcceptInvite && (
            <Button
              className="w-full font-bold text-base py-2.5"
              style={{
                background: "linear-gradient(180deg, #16a34a 0%, #15803d 100%)",
                border: "2px solid #22c55e",
                boxShadow: "0 4px 15px rgba(34,197,94,0.4)"
              }}
              onClick={onAcceptInvite}
            >
              <Check className="w-5 h-5 mr-2" />
              Accept Invitation
            </Button>
          )}

          {/* Leave Expedition button (when user is a member but not leader) */}
          {isMember && !isLeader && onLeave && (
            <Button
              className="w-full font-bold text-base py-2.5"
              variant="outline"
              style={{
                borderColor: "#dc2626",
                color: "#f87171"
              }}
              onClick={onLeave}
            >
              <X className="w-5 h-5 mr-2" />
              Leave Expedition
            </Button>
          )}
          
          {/* Apply button (only if not invited, not a member, not a participant) */}
          {!hasPendingInvite && !isMember && !isLeader && !isParticipant && expedition.status === "ACTIVE" && (
            <Button
              className="w-full font-bold text-base py-2.5"
              style={{
                background: "linear-gradient(180deg, #7c4dff 0%, #5c35cc 100%)",
                border: "2px solid #9c7cff",
                boxShadow: "0 4px 15px rgba(124,77,255,0.4)"
              }}
              onClick={onApply}
            >
              <UserPlus className="w-5 h-5 mr-2" />
              Apply to Join
            </Button>
          )}
          
          {/* Apply as Buyer - only if selling service is enabled */}
          {!isLeader && !isMember && !isParticipant && expedition.status === "ACTIVE" && !expedition.client_id && onApplyAsClient && expedition.selling_service !== false && (
            <Button
              className="w-full font-bold text-base py-2.5"
              style={{
                background: "linear-gradient(180deg, #d97706 0%, #b45309 100%)",
                border: "2px solid #f59e0b",
                boxShadow: "0 4px 15px rgba(217,119,6,0.4)"
              }}
              onClick={onApplyAsClient}
              data-testid="expedition-apply-client-btn"
            >
              <DollarSign className="w-5 h-5 mr-2" />
              Join as Buyer
            </Button>
          )}
          
          {/* View details */}
          <Button
            variant="outline"
            className="w-full border-blue-500/50 text-blue-300 hover:bg-blue-500/10 font-semibold"
            onClick={onViewDetails}
          >
            <Eye className="w-4 h-4 mr-2" />
            View Details
          </Button>
          
          {/* Admin cancel button */}
          {isAdmin && expedition.status === "ACTIVE" && (
            <Button
              variant="outline"
              className="w-full border-red-500 text-red-300 hover:bg-red-500/20"
              onClick={onAdminCancel}
            >
              <X className="w-4 h-4 mr-2" />
              Admin Cancel
            </Button>
          )}
        </div>
        
        {/* Client indicator */}
        {expedition.client_username && (
          <div className="absolute bottom-3 left-3" onClick={(e) => e.stopPropagation()}>
            <Badge
              className="text-xs bg-amber-500/20 border-amber-500/50 text-amber-300 cursor-pointer hover:bg-amber-500/30"
              onClick={() => onViewUser?.(expedition.client_id)}
              data-testid={`view-buyer-${expedition.id}`}
            >
              Buyer: {expedition.client_username}
            </Badge>
          </div>
        )}
        
        {/* Glowing bottom border */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-1"
          style={{ background: "linear-gradient(90deg, transparent, #7c4dff, transparent)" }}
        />
      </div>
    </div>
  );
};

export default ExpeditionPoster;
